import type { Case } from './initial-cases';
import { personName } from '../auth/actorNames';
import { unmetRequiredKyc } from './kyc-progress';
import { hasExecutedAgreement } from '../state/actions/onboarding';
import { loaOutstanding, missingRequiredFields, onboardingFormData } from '../pages/CaseDetail/tabs/OnboardingTab';
import { shaftReviewed } from '../pages/CaseDetail/tabs/RcdShaftTab';
import { ipVerified } from '../pages/CaseDetail/tabs/IpTab';
import { finalChangedHold, needsRecommendation } from '../pages/CaseDetail/tabs/DecisionTab';

// #766 — WHERE A CASE IS, AND WHAT IS STILL OPEN AT THAT STAGE.
//
// One derivation, read by the stepper and stage banner in the case header
// (components/CaseProgressTracker.tsx) and by the queues' "Stage / next step"
// column. There is no state machine behind it: the SPA PATCHes `status`
// directly and the API adds side effects on particular transitions, so the
// stage is READ off the fields those writes leave, in the order below.
//
// THE OPEN ITEMS IMPORT THE GATES' OWN PREDICATES. Every item is the question
// the control that resolves it already asks — the Submit Onboarding sweep
// (missingRequiredFields), the Send to vetting agreement check
// (hasExecutedAgreement), the LOA card's warning edge (loaOutstanding), the KYC
// evidence minimums (unmetRequiredKyc, #772), the SHAFT and IP ticks
// (shaftReviewed, ipVerified), the recommendation a decision needs
// (needsRecommendation, finalChangedHold, #781). A tracker that restated a
// gate would drift from it, and one that disagrees with a gate is worse than
// none. case-progress.test.ts proves each item flips with its gate.
//
// CTIA DELIVERY is read from `settlementSyncStatus`/`settlementSyncStep` and
// the `decision` column only — NOT a mirror of the API's `holdsApproval`
// (workers/settlement-delivery.ts), which the SPA cannot import. The stage is
// what the worker has recorded, not a prediction of what it would do — so the
// banners say "recorded as synced" and "sync requested, not yet confirmed",
// never "delivered": a sync the worker blocks or dead-letters stays at
// PENDING_SYNC, and a SYNCED case whose approval is later revoked keeps the
// SYNCED it last recorded. The stage is only entered where an approval
// stands: status SUCCESSFUL, or FINAL_DETERMINATION with finalDetermination
// CHANGED (an appeal that overturned the denial). Any other status — a cure,
// an appeal, CLOSED, PENDING_DELETION, or a final determination that left the
// decision UNCHANGED or UPHELD_UNSUCCESSFUL — shows that status's stage, not a
// delivery. That is the reading `holdsApproval` makes today, written out here
// and NOT pinned to it (lead ruling on #766): if the API's rule changes, this
// line does not follow it by itself.

export const CASE_STAGES = [
  { key: 'received', label: 'Application received', detail: 'application data' },
  { key: 'onboarding', label: 'Onboarding', detail: 'agreement, LOA, documents' },
  { key: 'vetting', label: 'Vetting', detail: 'KYC, RCD/SHAFT, IP, Trust, TNs' },
  { key: 'recommendation', label: 'Recommendation', detail: 'the OA/VA Agent recommends' },
  { key: 'decision', label: 'Decision', detail: 'an approver decides' },
  { key: 'delivery', label: 'CTIA delivery', detail: 'enterprise → DIR → numbers' },
] as const;

export type CaseStage = (typeof CASE_STAGES)[number]['key'];

/** A detour off Decision: shown as a badge, not a stage on the main line. */
export type CaseSideState = 'cure' | 'appeal' | 'final';

export const SIDE_STATE_LABELS: Record<CaseSideState, string> = {
  cure: 'Cure window',
  appeal: 'Appeal',
  final: 'Final determination',
};

export interface OpenItem {
  key: string;
  label: string;
  /** The case tab where the item is resolved; absent when no person resolves it (the sync worker's steps). */
  tab?: string;
}

export interface CaseProgress {
  stage: CaseStage;
  /** The current stage itself is complete: the case is decided, or delivered. */
  done: boolean;
  sideState?: CaseSideState;
  /** The case has left the line altogether. */
  ended?: 'CLOSED' | 'PENDING_DELETION';
  openItems: OpenItem[];
  /** The stage banner, e.g. "VETTING — under review". */
  banner: string;
}

const SIDE_STATE_BY_STATUS: Record<string, CaseSideState> = {
  CURE_WINDOW: 'cure',
  APPEAL: 'appeal',
  FINAL_DETERMINATION: 'final',
};

const SIDE_STATE_BANNERS: Record<CaseSideState, string> = {
  cure: 'CURE WINDOW — waiting on the registrant',
  appeal: 'APPEAL — under reassessment',
  final: 'FINAL DETERMINATION — the appeal is decided',
};

// The CTIA delivery pipeline's committed steps, in order (cases.settlement_sync_step).
const SYNC_STEPS = [
  { step: 'ENTERPRISE_REGISTERED', label: 'Enterprise registration' },
  { step: 'DIR_UPLOADED', label: 'DIR upload' },
  { step: 'TNS_UPLOADED', label: 'Numbers upload' },
] as const;

function kycItems(c: Case, prefix = ''): OpenItem[] {
  return unmetRequiredKyc(c).map(u => ({
    key: `kyc-${u.key}`,
    label: `${prefix}${u.label} — ${u.progress}`,
    tab: 'kyc',
  }));
}

function vettingStages(c: Case): Pick<CaseProgress, 'stage' | 'openItems' | 'banner'> {
  if (!needsRecommendation(c)) {
    const held = c.recommendation === 'APPROVE' ? kycItems(c, 'Approval held: ') : [];
    return {
      stage: 'decision',
      openItems: [{ key: 'decide', label: 'An approver records the decision', tab: 'decision' }, ...held],
      banner: 'DECISION — awaiting an approver',
    };
  }
  const open: OpenItem[] = [...kycItems(c)];
  if (!shaftReviewed(c)) open.push({ key: 'shaft', label: 'Complete the SHAFT review (logo and content)', tab: 'rcd-shaft' });
  if (!ipVerified(c)) open.push({ key: 'ip', label: 'Attest the IP search', tab: 'ip' });
  if (open.length > 0) return { stage: 'vetting', openItems: open, banner: 'VETTING — under review' };
  return {
    stage: 'recommendation',
    openItems: [{ key: 'recommend', label: 'The case’s OA/VA Agent recommends approve or deny', tab: 'decision' }],
    banner: 'VETTING — awaiting the OA/VA Agent’s recommendation',
  };
}

function onboardingStages(c: Case): Pick<CaseProgress, 'stage' | 'openItems' | 'banner'> {
  const ob = (c.onboarding ?? {}) as { status?: string; data?: Record<string, unknown>; intake?: Record<string, unknown> };
  if (ob.status !== 'SUBMITTED' && ob.status !== 'READY_FOR_VETTING') {
    const missing = missingRequiredFields(onboardingFormData(ob));
    const open: OpenItem[] = [];
    if (missing.length > 0) {
      open.push({
        key: 'required-fields',
        label: `${missing.length} required field${missing.length === 1 ? '' : 's'} empty: ${missing.join(', ')}`,
        tab: 'onboarding',
      });
    }
    open.push({ key: 'submit', label: 'Submit the onboarding data', tab: 'onboarding' });
    return { stage: 'received', openItems: open, banner: 'APPLICATION RECEIVED — completing the application data' };
  }
  const open: OpenItem[] = [];
  if (!hasExecutedAgreement(c)) open.push({ key: 'agreement', label: 'Confirm the executed Registrant Agreement', tab: 'onboarding' });
  if (loaOutstanding(c)) open.push({ key: 'loa', label: 'Verify the Letter of Authorization', tab: 'onboarding' });
  open.push({ key: 'send', label: 'Send to vetting', tab: 'onboarding' });
  return { stage: 'onboarding', openItems: open, banner: 'ONBOARDING — agreement, LOA and documents' };
}

export function caseProgress(c: Case): CaseProgress {
  const sideState = SIDE_STATE_BY_STATUS[c.status];
  const ended = c.status === 'CLOSED' || c.status === 'PENDING_DELETION' ? c.status : undefined;
  let core: Pick<CaseProgress, 'stage' | 'openItems' | 'banner'> & { done?: boolean };

  if (c.decision === 'SUCCESSFUL' && c.settlementSyncStatus != null
    && (c.status === 'SUCCESSFUL' || (c.status === 'FINAL_DETERMINATION' && c.finalDetermination === 'CHANGED'))) {
    const synced = c.settlementSyncStatus === 'SYNCED';
    const reached = SYNC_STEPS.findIndex(s => s.step === c.settlementSyncStep);
    const open: OpenItem[] = synced ? [] : SYNC_STEPS.slice(reached + 1).map(s => ({ key: `sync-${s.step}`, label: s.label }));
    if (!synced && open.length === 0) open.push({ key: 'sync-confirm', label: 'Sync completion' });
    core = {
      stage: 'delivery',
      done: synced,
      openItems: open,
      banner: synced ? 'CTIA DELIVERY — recorded as synced' : 'CTIA DELIVERY — sync requested, not yet confirmed',
    };
  } else if (c.status === 'IN_REVIEW') {
    // Includes a case back in vetting after a cure: its earlier decision stays
    // on the row, and it is worked again exactly like a first vetting.
    core = vettingStages(c);
  } else if (c.decision != null || sideState) {
    const hold = c.status === 'APPEAL' ? finalChangedHold(c) : null;
    const open: OpenItem[] = !hold ? []
      : hold.reason === 'recommendation'
        ? [{ key: 'appeal-recommend', label: 'Final: Changed needs a new recommendation from the OA/VA Agent', tab: 'decision' }]
        : kycItems(c, 'Final: Changed held: ');
    core = {
      stage: 'decision',
      done: !sideState && c.decision != null,
      openItems: open,
      banner: c.decision ? `DECIDED — ${c.decision.toLowerCase()}` : 'DECISION — awaiting an approver',
    };
  } else {
    core = onboardingStages(c);
  }

  return {
    stage: core.stage,
    done: core.done ?? false,
    ...(sideState ? { sideState } : {}),
    ...(ended ? { ended } : {}),
    openItems: ended ? [] : core.openItems,
    // A detour's banner, except once an overturned decision is being
    // delivered: the delivery is then what is happening, and the badge still
    // says how the case got there.
    banner: ended ? ended.replace('_', ' ')
      : sideState && core.stage !== 'delivery' ? SIDE_STATE_BANNERS[sideState] : core.banner,
  };
}

/** The stage's label, for the queues' "Stage / next step" column and the stepper. */
export function stageLabel(stage: CaseStage): string {
  return CASE_STAGES.find(s => s.key === stage)!.label;
}

/** "Sent to vetting by Sam Sender on …", or null before the handoff. */
export function handoffLine(
  c: Pick<Case, 'vettingEnteredAt' | 'handedToVettingBy' | 'handedToVettingByName'>,
  fmt: (iso: string) => string,
): string | null {
  if (!c.vettingEnteredAt) return null;
  const when = fmt(c.vettingEnteredAt);
  // Neither is set on a case that entered vetting before #766 and whose
  // handoff A030 could not recover from the audit trail.
  if (!c.handedToVettingBy && !c.handedToVettingByName) return `Sent to vetting on ${when} — sender not recorded`;
  return `Sent to vetting by ${personName(c.handedToVettingBy, c.handedToVettingByName)} on ${when}`;
}

/**
 * Has the case been sent to vetting, by any door? Send to vetting marks the
 * onboarding blob READY_FOR_VETTING; every entry into IN_REVIEW (Send to
 * vetting, a create straight into review, an API client) stamps
 * `vettingEnteredAt`, which A028 backfilled on every case already past
 * onboarding. Either one takes the case off the Onboarding queue.
 */
export function isSentToVetting(c: Pick<Case, 'onboarding' | 'vettingEnteredAt'>): boolean {
  return c.onboarding?.status === 'READY_FOR_VETTING' || !!c.vettingEnteredAt;
}

/**
 * The Cases list's "New" badge: sent to vetting and not yet opened by its
 * OA/VA Agent (`vettingOpenedAt`, written by POST /cases/:id/vetting-opened).
 * Server-side, so it is the same for every viewer.
 */
export function isNewToVetting(c: Pick<Case, 'vettingEnteredAt' | 'vettingOpenedAt'>): boolean {
  return !!c.vettingEnteredAt && !c.vettingOpenedAt;
}
